import type { CaseStatus } from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';

export interface CaseUpdate {
  id: string;
  date: string;
  title: string;
  description: string;
  author: string;
}

export interface Attorney {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string;
}

export interface AttorneyFull extends Attorney {
  photoUrl: string;
  caseload: number;
}

export interface Case {
  id: string;
  caseNumber: string;
  claimantName: string;
  documentNumber: string;
  documentType: 'Cédula de Ciudadanía' | 'NIT' | 'Pasaporte' | 'Cédula Extranjería';
  email: string;
  phone: string;
  caseType: string;
  court: string;
  description: string;
  status: CaseStatus;
  estimatedRecovery: number;
  currency: 'COP' | 'USD';
  missingDocumentsFee: number;
  adminMessage: string | null;
  paymentDueDate: string | null;
  assignedAttorney: Attorney;
  updates: CaseUpdate[];
  createdAt: string;
  updatedAt: string;
}

// ── Row types from Supabase ──────────────────────────────────────────────────
interface AttorneyRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  photo_url?: string;
  caseload?: number;
}

interface CaseUpdateRow {
  id: string;
  case_id: string;
  update_date: string;
  title: string;
  description: string;
  author: string;
}

interface CaseRow {
  id: string;
  case_number: string;
  claimant_name: string;
  document_number: string;
  document_type: string;
  email: string;
  phone: string;
  case_type: string;
  court: string;
  description: string;
  status: string;
  estimated_recovery: number;
  currency: string;
  missing_documents_fee: number;
  admin_message: string | null;
  payment_due_date: string | null;
  attorney_id: string | null;
  created_at: string;
  updated_at: string;
  attorneys: AttorneyRow | null;
  case_updates: CaseUpdateRow[];
}

// ── Mappers ──────────────────────────────────────────────────────────────────
function mapAttorney(row: AttorneyRow): Attorney {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    specialization: row.specialization,
  };
}

function mapAttorneyFull(row: AttorneyRow): AttorneyFull {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    phone: row.phone,
    specialization: row.specialization,
    photoUrl: row.photo_url ?? '',
    caseload: row.caseload ?? 0,
  };
}

function mapUpdate(row: CaseUpdateRow): CaseUpdate {
  return {
    id: row.id,
    date: row.update_date,
    title: row.title,
    description: row.description,
    author: row.author,
  };
}

function mapCase(row: CaseRow): Case {
  const fallbackAttorney: Attorney = {
    id: row.attorney_id ?? '',
    name: 'Sin asignar',
    email: '',
    phone: '',
    specialization: '',
  };
  return {
    id: row.id,
    caseNumber: row.case_number,
    claimantName: row.claimant_name,
    documentNumber: row.document_number,
    documentType: row.document_type as Case['documentType'],
    email: row.email,
    phone: row.phone,
    caseType: row.case_type,
    court: row.court,
    description: row.description,
    status: row.status as CaseStatus,
    estimatedRecovery: Number(row.estimated_recovery),
    currency: row.currency as 'COP' | 'USD',
    missingDocumentsFee: Number(row.missing_documents_fee),
    adminMessage: row.admin_message ?? null,
    paymentDueDate: row.payment_due_date ?? null,
    assignedAttorney: row.attorneys ? mapAttorney(row.attorneys) : fallbackAttorney,
    updates: (row.case_updates ?? [])
      .map(mapUpdate)
      .sort((a, b) => (a.date < b.date ? 1 : -1)),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ── Public API ───────────────────────────────────────────────────────────────

export async function getCases(): Promise<Case[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cases')
    .select('*, attorneys(*), case_updates(*)')
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('getCases error:', error.message);
    return [];
  }
  return (data as CaseRow[]).map(mapCase);
}

export async function getCaseById(id: string): Promise<Case | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('cases')
    .select('*, attorneys(*), case_updates(*)')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    console.error('getCaseById error:', error.message);
    return null;
  }
  return data ? mapCase(data as CaseRow) : null;
}

export async function findCaseByDocument(docNumber: string): Promise<Case | null> {
  const supabase = createClient();
  const normalized = docNumber.replace(/[^0-9]/g, '');

  // Fetch all cases and filter client-side to handle NIT format (e.g. 900456123-1)
  const { data, error } = await supabase
    .from('cases')
    .select('*, attorneys(*), case_updates(*)');

  if (error) {
    console.error('findCaseByDocument error:', error.message);
    return null;
  }

  const found = (data as CaseRow[]).find(
    (row) => row.document_number.replace(/[^0-9]/g, '') === normalized
  );
  return found ? mapCase(found) : null;
}

export async function getAttorneys(): Promise<AttorneyFull[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('attorneys')
    .select('*')
    .order('name', { ascending: true });

  if (error) {
    console.error('getAttorneys error:', error.message);
    return [];
  }
  return (data as AttorneyRow[]).map(mapAttorneyFull);
}

export async function addAttorney(attorney: Omit<AttorneyFull, 'id'>): Promise<boolean> {
  const supabase = createClient();
  const id = `att-${Date.now()}`;
  const { error } = await supabase.from('attorneys').insert({
    id,
    name: attorney.name,
    email: attorney.email,
    phone: attorney.phone,
    specialization: attorney.specialization,
    photo_url: attorney.photoUrl,
    caseload: attorney.caseload,
  });
  if (error) {
    console.error('addAttorney error:', error.message);
    return false;
  }
  return true;
}

export async function updateAttorney(attorney: AttorneyFull): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase
    .from('attorneys')
    .update({
      name: attorney.name,
      email: attorney.email,
      phone: attorney.phone,
      specialization: attorney.specialization,
      photo_url: attorney.photoUrl,
      caseload: attorney.caseload,
    })
    .eq('id', attorney.id);
  if (error) {
    console.error('updateAttorney error:', error.message);
    return false;
  }
  return true;
}

export async function deleteAttorney(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('attorneys').delete().eq('id', id);
  if (error) {
    console.error('deleteAttorney error:', error.message);
    return false;
  }
  return true;
}

export async function addCase(newCase: Case): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();

  // Only insert the attorney if it doesn't already exist — never overwrite existing attorney data
  const { error: attErr } = await supabase
    .from('attorneys')
    .upsert(
      {
        id: newCase.assignedAttorney.id,
        name: newCase.assignedAttorney.name,
        email: newCase.assignedAttorney.email,
        phone: newCase.assignedAttorney.phone,
        specialization: newCase.assignedAttorney.specialization,
      },
      { onConflict: 'id', ignoreDuplicates: true }
    );
  if (attErr) console.error('upsert attorney error:', attErr.message);

  const { error: caseErr } = await supabase.from('cases').insert({
    id: newCase.id,
    case_number: newCase.caseNumber,
    claimant_name: newCase.claimantName,
    document_number: newCase.documentNumber,
    document_type: newCase.documentType,
    email: newCase.email,
    phone: newCase.phone,
    case_type: newCase.caseType,
    court: newCase.court,
    description: newCase.description,
    status: newCase.status,
    estimated_recovery: newCase.estimatedRecovery,
    currency: newCase.currency,
    missing_documents_fee: newCase.missingDocumentsFee,
    admin_message: newCase.adminMessage ?? null,
    payment_due_date: newCase.paymentDueDate ?? null,
    attorney_id: newCase.assignedAttorney.id,
    created_at: newCase.createdAt,
    updated_at: newCase.updatedAt,
  });

  if (caseErr) {
    console.error('addCase error:', caseErr.message);
    // Return a user-friendly error message
    if (caseErr.message.includes('unique') || caseErr.message.includes('duplicate') || caseErr.code === '23505') {
      return { success: false, error: `El número de expediente "${newCase.caseNumber}" ya existe. Por favor use un número diferente.` };
    }
    return { success: false, error: caseErr.message };
  }

  // Insert updates
  if (newCase.updates.length > 0) {
    const { error: updErr } = await supabase.from('case_updates').insert(
      newCase.updates.map((u) => ({
        id: u.id,
        case_id: newCase.id,
        update_date: u.date,
        title: u.title,
        description: u.description,
        author: u.author,
      }))
    );
    if (updErr) console.error('addCase updates error:', updErr.message);
  }

  return { success: true };
}

export async function updateCase(updatedCase: Case): Promise<boolean> {
  const supabase = createClient();

  // Only insert the attorney if it doesn't already exist — never overwrite existing attorney data
  const { error: attErr } = await supabase
    .from('attorneys')
    .upsert(
      {
        id: updatedCase.assignedAttorney.id,
        name: updatedCase.assignedAttorney.name,
        email: updatedCase.assignedAttorney.email,
        phone: updatedCase.assignedAttorney.phone,
        specialization: updatedCase.assignedAttorney.specialization,
      },
      { onConflict: 'id', ignoreDuplicates: true }
    );
  if (attErr) console.error('upsert attorney error:', attErr.message);

  // Update case row
  const { error: caseErr } = await supabase
    .from('cases')
    .update({
      case_number: updatedCase.caseNumber,
      claimant_name: updatedCase.claimantName,
      document_number: updatedCase.documentNumber,
      document_type: updatedCase.documentType,
      email: updatedCase.email,
      phone: updatedCase.phone,
      case_type: updatedCase.caseType,
      court: updatedCase.court,
      description: updatedCase.description,
      status: updatedCase.status,
      estimated_recovery: updatedCase.estimatedRecovery,
      currency: updatedCase.currency,
      missing_documents_fee: updatedCase.missingDocumentsFee,
      admin_message: updatedCase.adminMessage ?? null,
      payment_due_date: updatedCase.paymentDueDate ?? null,
      attorney_id: updatedCase.assignedAttorney.id,
      updated_at: updatedCase.updatedAt,
    })
    .eq('id', updatedCase.id);

  if (caseErr) {
    console.error('updateCase error:', caseErr.message);
    return false;
  }

  // Replace all updates: delete existing then re-insert
  await supabase.from('case_updates').delete().eq('case_id', updatedCase.id);

  if (updatedCase.updates.length > 0) {
    const { error: updErr } = await supabase.from('case_updates').insert(
      updatedCase.updates.map((u) => ({
        id: u.id,
        case_id: updatedCase.id,
        update_date: u.date,
        title: u.title,
        description: u.description,
        author: u.author,
      }))
    );
    if (updErr) console.error('updateCase updates error:', updErr.message);
  }

  return true;
}

export async function deleteCase(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('cases').delete().eq('id', id);
  if (error) {
    console.error('deleteCase error:', error.message);
    return false;
  }
  return true;
}

export async function getCaseCount(): Promise<number> {
  const supabase = createClient();
  const { count, error } = await supabase
    .from('cases')
    .select('*', { count: 'exact', head: true });
  if (error) return 0;
  return count ?? 0;
}

// ── Case Documents ────────────────────────────────────────────────────────────

export interface CaseDocument {
  id: string;
  caseId: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadedAt: string;
}

interface CaseDocumentRow {
  id: string;
  case_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  uploaded_at: string;
}

function mapDocument(row: CaseDocumentRow): CaseDocument {
  return {
    id: row.id,
    caseId: row.case_id,
    fileName: row.file_name,
    filePath: row.file_path,
    fileSize: row.file_size,
    uploadedAt: row.uploaded_at,
  };
}

export async function getCaseDocuments(caseId: string): Promise<CaseDocument[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('case_documents')
    .select('*')
    .eq('case_id', caseId)
    .order('uploaded_at', { ascending: false });
  if (error) {
    console.error('getCaseDocuments error:', error.message);
    return [];
  }
  return (data as CaseDocumentRow[]).map(mapDocument);
}

export async function uploadCaseDocument(
  caseId: string,
  file: File
): Promise<CaseDocument | null> {
  const supabase = createClient();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${caseId}/${Date.now()}_${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from('case-documents')
    .upload(filePath, file, { contentType: 'application/pdf', upsert: false });

  if (uploadError) {
    console.error('uploadCaseDocument storage error:', uploadError.message);
    return null;
  }

  const { data: insertData, error: insertError } = await supabase
    .from('case_documents')
    .insert({
      case_id: caseId,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
    })
    .select()
    .single();

  if (insertError) {
    console.error('uploadCaseDocument insert error:', insertError.message);
    return null;
  }

  return mapDocument(insertData as CaseDocumentRow);
}

export async function deleteCaseDocument(doc: CaseDocument): Promise<boolean> {
  const supabase = createClient();

  const { error: storageError } = await supabase.storage
    .from('case-documents')
    .remove([doc.filePath]);

  if (storageError) {
    console.error('deleteCaseDocument storage error:', storageError.message);
  }

  const { error: dbError } = await supabase
    .from('case_documents')
    .delete()
    .eq('id', doc.id);

  if (dbError) {
    console.error('deleteCaseDocument db error:', dbError.message);
    return false;
  }
  return true;
}

export function getCaseDocumentUrl(filePath: string): string {
  const supabase = createClient();
  const { data } = supabase.storage.from('case-documents').getPublicUrl(filePath);
  return data.publicUrl;
}

// ── Formatting helpers (unchanged) ──────────────────────────────────────────
export function formatCurrency(amount: number, currency: 'COP' | 'USD' = 'USD'): string {
  if (currency === 'COP') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
}

// Static attorneys list for form dropdowns (seeded in DB, kept here for convenience)
export const attorneys: Attorney[] = [
  { id: 'att-001', name: 'Dr. Andrés Morales Restrepo', email: 'a.morales@legalstatus.co', phone: '+1 (305) 345-6789', specialization: 'Derecho Financiero' },
  { id: 'att-002', name: 'Dra. Catalina Ríos Vargas', email: 'c.rios@legalstatus.co', phone: '+1 (305) 345-6790', specialization: 'Litigios Civiles' },
  { id: 'att-003', name: 'Dr. Felipe Gutiérrez Mora', email: 'f.gutierrez@legalstatus.co', phone: '+1 (305) 345-6791', specialization: 'Derecho Comercial' },
  { id: 'att-004', name: 'Dra. Valentina Ospina Cano', email: 'v.ospina@legalstatus.co', phone: '+1 (305) 345-6792', specialization: 'Recuperación de Activos' },
];
